// WHAT THE LAST FRAME ACTUALLY ASKED THE GPU FOR, BY KIND OF OBJECT.
//
// renderer.info says how many calls and triangles a frame cost; it cannot
// say WHO spent them. Round 8 at rest (Tom, real GPU, SpaceX): 12-14 M
// triangles and 5.1-5.9 k calls against a plan of 8 M / 2.6 k, and the only
// way to find the difference was a one-off headless script. This is that
// script, kept: one walk of the scene, on demand only (perfSnapshot), never
// in the tick.
//
// Per category: objects, calls and triangles as three counts them.
//   pool      one InstancedMesh per (hash, LOD). A visible pool is ONE call
//             and draws mesh.count instances - INCLUDING free holes below
//             the tail and parked (frustum-hidden) slots, which are
//             zero-scaled but still vertex work and still in
//             renderer.info.triangles. Those are split out (holeTris,
//             parkedTris) so "over budget" can be blamed on the right thing.
//   ghostTwin the see-through copy of a pool; drawn only while something in
//             it is ghosted.
//   batch     a tile's BatchedMesh. One call when any item survives its
//             per-item cull, and only the surviving items' triangles - read
//             from the multi-draw list three built for the last frame.
//   merge     a tile's merged singleton mesh (drawRange prefix).
//   other     everything else in the scene (sky dome, grid, overlays).
//
// A material that is transparent AND double-sided is drawn by three in
// TWO calls (back faces, then front faces) unless forceSinglePass is set
// - see blendPass.js. Such an object is counted twice here, as the GPU
// sees it, and the doubling is reported on its own (byKind.twoPass), so
// a passFactor above 1 means a pass the census does not know about.
//
// And a histogram of the visible pools by live instance count: the call
// a pool costs is the same for 1 instance as for 10,000, so the low
// buckets are the calls instancing is not paying for.
import { drawsTwice } from './blendPass';

const BUCKETS = [[1, 1], [2, 2], [3, 4], [5, 9], [10, 31], [32, 99], [100, Infinity]];

function bucketLabel([a, b]) {
  if (a === b) return String(a);
  return b === Infinity ? `${a}+` : `${a}-${b}`;
}

function trianglesOf(geometry) {
  if (!geometry) return 0;
  if (geometry.index) return geometry.index.count / 3;
  const p = geometry.attributes && geometry.attributes.position;
  return p ? p.count / 3 : 0;
}

function shown(object) {
  for (let o = object; o; o = o.parent) if (o.visible === false) return false;
  return true;
}

function batchRole(mesh) {
  const ud = mesh.userData || {};
  if (ud.batchRole) return ud.batchRole;
  return /#v\d/.test(mesh.name || '') ? 'virtual' : 'tile';
}

// scene: the viewer's scene. poolRegistry: for holes and parked slots per
// pool (optional - without it every slot below count counts as live).
export default function drawCensus({ scene, poolRegistry = null, gpu = null } = {}) {
  const cats = {};
  const twoPass = { objects: 0, calls: 0, tris: 0 };
  let passes = 1;
  const add = (key, calls, tris, extra) => {
    const e = cats[key] || (cats[key] = { objects: 0, calls: 0, tris: 0 });
    e.objects += 1;
    e.calls += calls * passes;
    e.tris += Math.round(tris * passes);
    if (passes > 1 && calls > 0) {
      twoPass.objects += 1;
      twoPass.calls += calls * (passes - 1);
      twoPass.tris += Math.round(tris * (passes - 1));
    }
    if (extra) for (const k of Object.keys(extra)) e[k] = (e[k] || 0) + Math.round(extra[k]);
  };
  const hist = BUCKETS.map((b) => ({
    live: bucketLabel(b), pools: 0, tris: 0, liveTris: 0, holeTris: 0, parkedTris: 0, instances: 0,
  }));
  const bucketOf = (n) => {
    for (let i = 0; i < BUCKETS.length; i++) if (n >= BUCKETS[i][0] && n <= BUCKETS[i][1]) return hist[i];
    return null;
  };

  if (scene && typeof scene.traverse === 'function') {
    scene.traverse((o) => {
      if (!o || o === scene || !shown(o)) return;
      passes = drawsTwice(Array.isArray(o.material) ? o.material[0] : o.material) ? 2 : 1;
      if (o.isBatchedMesh) {
        const n = o._multiDrawCount || 0;
        let indices = 0;
        const counts = o._multiDrawCounts;
        if (counts) for (let i = 0; i < n; i++) indices += counts[i];
        const kind = o.material && o.material.transparent ? 'blend' : 'opaque';
        add(`batch:${batchRole(o)}:${kind}`, n > 0 ? 1 : 0, indices / 3, {
          items: o.batchItemCount || 0, drawnItems: n, heldTris: trianglesOf(o.geometry),
        });
        return;
      }
      if (o.isInstancedMesh) {
        const each = trianglesOf(o.geometry);
        const count = o.count || 0;
        if (/_ghost$/.test(o.name || '')) { add('ghostTwin', count > 0 ? 1 : 0, each * count); return; }
        const ud = o.userData || {};
        if (!ud.hash) { add(`instanced:${(o.name || '?').slice(0, 24)}`, count > 0 ? 1 : 0, each * count); return; }
        const pool = poolRegistry && typeof poolRegistry.get === 'function' ? poolRegistry.get(ud.hash, ud.currentLod) : null;
        const holes = pool && pool.freeSlots ? pool.freeSlots.filter((i) => i < count).length : 0;
        const parked = pool && pool.hiddenSlots ? pool.hiddenSlots.size : 0;
        const live = Math.max(0, count - holes);
        add('pool', count > 0 ? 1 : 0, each * count, {
          liveTris: each * Math.max(0, live - parked), holeTris: each * holes, parkedTris: each * parked, instances: live,
        });
        const b = count > 0 ? bucketOf(live) : null;
        if (b) {
          b.pools += 1;
          b.tris += Math.round(each * count);
          b.liveTris += Math.round(each * Math.max(0, live - parked));
          b.holeTris += Math.round(each * holes);
          b.parkedTris += Math.round(each * parked);
          b.instances += live;
        }
        return;
      }
      if (o.isMesh) {
        let t = trianglesOf(o.geometry);
        const dr = o.geometry && o.geometry.drawRange;
        if (dr && Number.isFinite(dr.count)) t = Math.min(t, dr.count / 3);
        const key = o.isSlotMesh ? 'merge' : `mesh:${(o.name || '?').slice(0, 24)}`;
        add(key, 1, t);
        return;
      }
      if (o.isLine || o.isLineSegments || o.isPoints || o.isSprite) add(`other:${o.type}`, 1, 0);
    });
  }

  let calls = 0;
  let tris = 0;
  for (const k of Object.keys(cats)) { calls += cats[k].calls; tris += cats[k].tris; }
  // Folded views: what the call budget is about.
  const sum = (prefix, field) => Object.keys(cats).filter((k) => k.startsWith(prefix))
    .reduce((s, k) => s + (cats[k][field] || 0), 0);
  const out = {
    calls,
    tris,
    byKind: {
      pools: { calls: sum('pool', 'calls'), tris: sum('pool', 'tris'), holeTris: sum('pool', 'holeTris'), parkedTris: sum('pool', 'parkedTris') },
      batches: { calls: sum('batch:', 'calls'), tris: sum('batch:', 'tris'), objects: sum('batch:', 'objects') },
      ghostTwins: { calls: sum('ghostTwin', 'calls'), tris: sum('ghostTwin', 'tris') },
      merges: { calls: sum('merge', 'calls'), tris: sum('merge', 'tris') },
      other: { calls: calls - sum('pool', 'calls') - sum('batch:', 'calls') - sum('ghostTwin', 'calls') - sum('merge', 'calls') },
      // Already inside the counts above: the second call of each
      // transparent double-sided object.
      twoPass,
    },
    categories: cats,
    poolsByInstances: hist,
  };
  // The scene is walked once; the GPU may have walked it more than once
  // (a depth prepass, a composer pass). Their ratio says so.
  if (gpu && gpu.calls) {
    out.gpu = { calls: gpu.calls, triangles: gpu.triangles };
    out.passFactor = calls > 0 ? +(gpu.calls / calls).toFixed(2) : null;
  }
  return out;
}
